/**
 * SOLOMON INPUT BAR
 * The command center at the bottom of the chat.
 * Supports text input, voice activation, file upload, and mode-aware placeholder.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { attachFiles as routeAttachFiles } from './attachFiles';
import './chat.css';

const MODE_PLACEHOLDERS = {
  conversation: 'Talk to Solomon...',
  command:      'Give Solomon a command...',
  research:     'What do you want Solomon to research?',
  creative:     'Let\'s brainstorm...',
  analysis:     'What should Solomon analyze?',
};

export default function InputBar({
  onSend,
  onStop,
  onVoiceToggle,
  onFileUpload,
  onCancelUpload,
  onDocumentAttach,
  attachments    = [],
  onClearAttachment,
  onClearAllAttachments,
  onImageAttach,
  pendingImages  = [],
  onClearPendingImage,
  isListening    = false,
  isThinking     = false,
  activeMode     = 'conversation',
  disabled       = false,
  interimText    = '',
  sttIsSupported = true,
  uploadProgress = null,  // { stage, percent } or null
  pendingDraft   = null,  // Task 3: a prior user message to load into the box for editing
  onDraftConsumed,        // clear the pending draft once injected (prevents re-inject)
}) {
  const [value,    setValue]    = useState('');
  const [focused,  setFocused]  = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCountRef = useRef(0);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);

  // Auto-focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Inject a pending draft once, then clear it via onDraftConsumed so it does
  // not re-inject on subsequent renders. The auto-resize effect above keys off
  // `value`, so the injected text resizes the textarea automatically.
  //
  // APPEND, never replace. Edit-and-resend (Task 3) only ever fired into an
  // EMPTY composer, so a replace was safe there. Capability prefill fires at
  // "wait, what was the phrasing?" — i.e. exactly when the composer is likely
  // half-written — and must not destroy what is already typed. The functional
  // form reads the current value without making it an effect dependency, which
  // would re-run the injection on every keystroke.
  useEffect(() => {
    if (pendingDraft == null) return;
    setValue((current) => (current.trim() ? `${current.trimEnd()}\n${pendingDraft}` : pendingDraft));
    textareaRef.current?.focus();
    onDraftConsumed?.();
  }, [pendingDraft, onDraftConsumed]);

  // Clear file error after 4 seconds
  useEffect(() => {
    if (!fileError) return;
    const t = setTimeout(() => setFileError(null), 4000);
    return () => clearTimeout(t);
  }, [fileError]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isThinking || disabled) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [value, isThinking, disabled, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Route files through the shared helper (single source of truth for the
  // image/doc/audio split + one-document-per-drop guard + accepted file-type rules).
  const attachFiles = useCallback((files) => {
    routeAttachFiles(files, {
      onImageAttach,
      onDocumentAttach,
      onFileUpload,
      onError: setFileError,
    });
  }, [onImageAttach, onDocumentAttach, onFileUpload]);

  const handleFileInputChange = (e) => {
    attachFiles(Array.from(e.target.files || []));
    e.target.value = '';  // reset so same file can be re-selected
  };

  // Paste-from-clipboard: screenshots and copied images route through the same
  // shared helper. Text pastes are untouched — we only intercept when the
  // clipboard actually carries image files.
  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (imageFiles.length === 0) return;
    e.preventDefault();
    attachFiles(imageFiles);
  };

  // Drag-and-drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current++;
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current <= 0) {
      dragCountRef.current = 0;
      setDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current = 0;
    setDragging(false);
    attachFiles(Array.from(e.dataTransfer.files || []));
  };

  const canSend = value.trim().length > 0 && !isThinking && !disabled;
  const isUploading = uploadProgress !== null;

  return (
    <div
      className={`input-bar ${focused ? 'input-bar--focused' : ''} ${isListening ? 'input-bar--listening' : ''} ${dragging ? 'input-bar--dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {dragging && (
        <div className="input-drag-overlay">
          <UploadCloudIcon />
          <span>Drop a file to attach</span>
        </div>
      )}

      {/* Upload progress bar */}
      {isUploading && (
        <div className="input-upload-progress">
          <div className="input-upload-progress-bar">
            <div
              className="input-upload-progress-fill"
              style={{ width: `${uploadProgress.percent || 0}%` }}
            />
          </div>
          <span className="input-upload-progress-label">{uploadProgress.stage || 'Processing...'}</span>
          {onCancelUpload && uploadProgress.stage !== 'Upload cancelled' && (
            <button
              className="input-upload-cancel"
              onClick={onCancelUpload}
              title="Cancel upload"
              aria-label="Cancel upload"
            >
              &#10005;
            </button>
          )}
        </div>
      )}

      {/* File error */}
      {fileError && (
        <div className="input-file-error">{fileError}</div>
      )}

      {/* Voice button */}
      {sttIsSupported && (
        <button
          className={`input-voice-btn ${isListening ? 'input-voice-btn--active' : ''}`}
          onClick={onVoiceToggle}
          title={isListening ? 'Stop listening' : 'Speak to Solomon'}
          aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
          disabled={isThinking || isUploading}
        >
          {isListening ? <VoiceActiveIcon /> : <VoiceIcon />}
        </button>
      )}

      {/* File upload button */}
      <button
        className="input-upload-btn"
        onClick={() => fileInputRef.current?.click()}
        title="Attach a file (image, document, or audio)"
        aria-label="Attach a file"
        disabled={isThinking || isUploading || disabled}
      >
        <PaperclipIcon />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif,.mp3,.mp4,.wav,.m4a,.webm,.ogg,.flac,audio/*,video/mp4,video/webm,.pdf,.txt,.md,.markdown,application/pdf,text/plain,text/markdown,.docx,.csv,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="input-file-hidden"
        onChange={handleFileInputChange}
        tabIndex={-1}
        multiple
      />

      {/* Image chips (one-turn-only — sent with the next message, then cleared) */}
      {pendingImages?.length > 0 && (
        <div className="image-chip-row">
          {pendingImages.map((im, i) => (
            <span className="image-chip" key={`${im.name}-${i}`} title={im.name}>
              <img
                className="image-chip-thumb"
                src={`data:${im.mediaType};base64,${im.dataBase64}`}
                alt={im.name}
              />
              <span className="image-chip-name">{im.name}</span>
              <button
                type="button"
                className="attach-chip-x"
                aria-label={`Remove ${im.name}`}
                onClick={() => onClearPendingImage?.(i)}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {/* Attachment chips (sticky for the conversation) */}
      {attachments?.length > 0 && (
        <div className="attach-chip-row">
          {attachments.map((a, i) => (
            <span className="attach-chip" key={`${a.name}-${i}`} title={a.name}>
              <span className="attach-chip-icon" aria-hidden="true"><PaperclipIcon /></span>
              <span className="attach-chip-name">{a.name}</span>
              <span className="attach-chip-meta">
                · {Math.round(a.charCount / 5).toLocaleString()} words{a.truncated ? ' (truncated to fit)' : ''}
              </span>
              <button type="button" className="attach-chip-x" aria-label={`Remove ${a.name}`} onClick={() => onClearAttachment?.(i)}>×</button>
            </span>
          ))}
          {attachments.length > 1 && (
            <button type="button" className="attach-clear-all" onClick={() => onClearAllAttachments?.()}>Clear attachments</button>
          )}
        </div>
      )}

      {/* Text input */}
      <div className="input-field-wrapper">
        {isListening && (
          <div className="input-listening-indicator">
            {interimText ? (
              <span className="input-interim-preview">{interimText}</span>
            ) : (
              <>
                <span>Listening</span>
                <span className="sol-filament" aria-hidden="true" />
              </>
            )}
          </div>
        )}

        <textarea
          ref={textareaRef}
          className="input-field"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={isUploading ? 'Transcribing file...' : (isListening ? '' : (MODE_PLACEHOLDERS[activeMode] || 'Talk to Solomon...'))}
          rows={1}
          disabled={disabled || isListening || isUploading}
          aria-label="Message input"
          aria-multiline="true"
        />
      </div>

      {/* Primary action: Stop while streaming, Send otherwise */}
      {isThinking ? (
        <button
          className="input-send-btn input-send-btn--stop input-send-btn--active"
          onClick={() => onStop?.()}
          title="Stop generating"
          aria-label="Stop generating"
        >
          <StopIcon />
        </button>
      ) : (
        <button
          className={`input-send-btn ${canSend ? 'input-send-btn--active' : ''}`}
          onClick={handleSend}
          disabled={!canSend}
          title="Send message (Enter)"
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      )}
    </div>
  );
}

// ============================================================
// ICONS — inline SVG for zero dependencies
// ============================================================

function VoiceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function VoiceActiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" fill="none" stroke="currentColor" strokeWidth="2" />
      <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" strokeWidth="2" />
      <line x1="8" y1="23" x2="16" y2="23" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function UploadCloudIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16l-4-4-4 4" />
      <path d="M12 12v9" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      <path d="M16 16l-4-4-4 4" />
    </svg>
  );
}
